import DefaultFormHeaderImage from '@/assets/images/form_default_header_image.png'
import Alert from '@/components/Alert/Alert'
import StatsGroup from '@/components/StatsGroup'
import TableView, { TableSection } from '@/components/TableView'
import { Heading } from '@/components/ui/heading'
import { Image } from '@/components/ui/image'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import useDefaultToast from '@/components/useDefaultToast'
import { useIsAppReviewMode } from '@/lib/hooks/useIsAppReviewMode'
import { selectFormSettingById, ShallowFormSetting, updateFormSettingThunk } from '@/redux/slices/formSettingSlice'
import {
  copyFormThunk,
  destroyFormThunk,
  fetchFormStatsThunk,
  formSelectors,
  ShallowForm,
  updateUserFormAppSettingThunk,
} from '@/redux/slices/formSlice'
import { openPublishSheet } from '@/redux/slices/publishSheetSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { ThemeHeaderType } from '@/typings/domain'
import { OnContentSizeChangeEvent } from '@modules/table-view'
import dayjs from 'dayjs'
import { BlurTargetView, BlurView } from 'expo-blur'
import { GlassView, isLiquidGlassAvailable } from 'expo-glass-effect'
import { LinearGradient } from 'expo-linear-gradient'
import { useFocusEffect, useRouter } from 'expo-router'
import { get } from 'lodash-es'
import { useColorScheme } from 'nativewind'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ImageSourcePropType, Platform, StyleSheet, View } from 'react-native'
import Animated, { SharedValue, useAnimatedStyle } from 'react-native-reanimated'

/**
 * Standard horizontal padding for iOS insetGrouped style TableView.
 * Used to keep other page content aligned with the TableView margins.
 */
const IOS_INSET_GROUPED_HORIZONTAL_PADDING = 20

function useFormHeaderImage(form: ShallowForm | undefined): ImageSourcePropType | null {
  return useMemo(() => {
    if (!form?.theme?.header) return null

    let imageUrl
    if (form.theme.header.type === ThemeHeaderType.Image) {
      imageUrl = form.theme.header.image?.visitUrl
    } else if (form.theme.header.type === ThemeHeaderType.Carousel) {
      imageUrl = form.theme.header.carouselImages?.[0]?.visitUrl
    }
    return imageUrl ? { uri: imageUrl } : DefaultFormHeaderImage
  }, [form?.theme])
}

const GLASS_FADE_HEIGHT = 120

function GlassCard({
  enabled,
  blurTargetRef,
  enabledAndroidBlur,
  children,
}: {
  enabled: boolean
  enabledAndroidBlur: boolean
  blurTargetRef?: React.RefObject<View | null>
  children: React.ReactNode
}) {
  const { colorScheme } = useColorScheme()
  const systemGroupedBg = colorScheme === 'dark' ? 'rgb(0,0,0)' : 'rgb(242,242,247)'
  const systemGroupedBgClear = colorScheme === 'dark' ? 'rgba(0,0,0,0)' : 'rgba(242,242,247,0)'

  if (!enabled) return <>{children}</>

  const backgroundStyle = [StyleSheet.absoluteFill, glassCardStyles.glassBackground]

  let glassBackground: React.ReactNode
  if (Platform.OS === 'ios' && isLiquidGlassAvailable()) {
    glassBackground = <GlassView style={backgroundStyle} />
  } else if (Platform.OS === 'web') {
    glassBackground = <View className="bg-system-grouped-background" style={backgroundStyle} />
  } else {
    glassBackground = (
      <BlurView
        tint={colorScheme === 'dark' ? 'dark' : 'light'}
        intensity={30}
        blurMethod="dimezisBlurView"
        {...(Platform.OS === 'android' && enabledAndroidBlur && { blurTarget: blurTargetRef })}
        style={backgroundStyle}
      />
    )
  }

  return (
    <View style={glassCardStyles.container}>
      {glassBackground}

      {Platform.OS !== 'web' && (
        <>
          <LinearGradient
            colors={[systemGroupedBgClear, systemGroupedBg]}
            style={[glassCardStyles.gradientOverlay, glassCardStyles.glassBackground]}
          />
          <View style={[glassCardStyles.solidOverlay, { backgroundColor: systemGroupedBg }]} />
        </>
      )}

      {children}
    </View>
  )
}

const glassCardStyles = StyleSheet.create({
  container: {
    marginTop: -28,
  },
  glassBackground: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: 'hidden',
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: GLASS_FADE_HEIGHT,
  },
  solidOverlay: {
    position: 'absolute',
    top: GLASS_FADE_HEIGHT,
    left: 0,
    right: 0,
    bottom: 0,
  },
})

export default function FormOverview({ formToken, scrollY }: { formToken: string; scrollY?: SharedValue<number> }) {
  const router = useRouter()
  const dispatch = useAppDispatch()

  const form = useAppSelector(state => formSelectors.selectById(state.forms, formToken))
  const formSetting = useAppSelector(state => selectFormSettingById(state, form?.token))
  const isCurrentUserManager = form?.isCurrentUserManager
  const headerImage = useFormHeaderImage(form)
  const [viewHorizontalPadding, setViewHorizontalPadding] = useState(IOS_INSET_GROUPED_HORIZONTAL_PADDING)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  const blurTargetRef = useRef<View>(null)
  const isFirstFocus = useRef(true)

  useFocusEffect(
    useCallback(() => {
      if (!isFirstFocus.current) {
        dispatch(fetchFormStatsThunk({ formToken }))
      } else {
        isFirstFocus.current = false
      }
    }, [dispatch, formToken])
  )

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true)
  }, [])

  if (!form || !formSetting || !formSetting.loaded) {
    return null
  }

  const hasHeaderImage = headerImage !== null

  return (
    <VStack>
      {Platform.OS === 'android' ? (
        <View style={{ position: 'relative' }}>
          <BlurTargetView ref={blurTargetRef} style={{ marginBottom: -200, paddingBottom: 200 }}>
            <FormHeaderImage
              headerImage={headerImage}
              alt={form.name ?? ''}
              scrollY={scrollY}
              onImageLoad={handleImageLoad}
            />
          </BlurTargetView>
        </View>
      ) : (
        <FormHeaderImage
          headerImage={headerImage}
          alt={form.name ?? ''}
          scrollY={scrollY}
          onImageLoad={handleImageLoad}
        />
      )}

      <GlassCard enabled={hasHeaderImage} enabledAndroidBlur={isImageLoaded} blurTargetRef={blurTargetRef}>
        <VStack
          space="lg"
          className={hasHeaderImage ? 'pt-6' : 'pt-5'}
          style={{ paddingLeft: viewHorizontalPadding, paddingRight: viewHorizontalPadding }}
        >
          <VStack space="sm">
            <Heading size="title-2" className="text-label">
              {form.displayName}
            </Heading>
            <Text className="text-subhead text-secondary-label">
              创建于 {dayjs(form.createdAt).format('YYYY-MM-DD HH:mm')}
            </Text>
          </VStack>

          <View className="bg-secondary-system-grouped-background rounded-3xl px-4 py-6 mt-4">
            <StatsGroup
              items={[
                {
                  title: form.stats?.todayFillCount ?? 0,
                  description: '今日数据',
                  onPress: () => router.push(`/forms/${formToken}/entries`),
                },
                {
                  title: form.stats?.todayViewCount ?? 0,
                  description: '今日浏览',
                  onPress: () => router.push(`/forms/${formToken}/entries`),
                },
                {
                  title: form.stats?.totalViewCount ?? 0,
                  description: '总浏览',
                  onPress: () => router.push(`/forms/${formToken}/entries`),
                },
                {
                  title: form.stats?.totalFillCount ?? 0,
                  description: '总数据',
                  onPress: () => router.push(`/forms/${formToken}/entries`),
                },
              ]}
            />
          </View>
        </VStack>

        <ManagementSection
          form={form}
          formSetting={formSetting}
          formToken={formToken}
          isCurrentUserManager={isCurrentUserManager}
          onTableViewContentSizeChange={e => {
            if (e.paddingLeft) setViewHorizontalPadding(e.paddingLeft)
          }}
        />
      </GlassCard>
    </VStack>
  )
}

type ManagementSectionProps = {
  form: ShallowForm
  formSetting: ShallowFormSetting
  formToken: string
  isCurrentUserManager?: boolean
  onTableViewContentSizeChange?: (event: OnContentSizeChangeEvent) => void
}

function ManagementSection({
  form,
  formSetting,
  formToken,
  isCurrentUserManager,
  onTableViewContentSizeChange,
}: ManagementSectionProps) {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const toast = useDefaultToast()
  const isAppReviewMode = useIsAppReviewMode()

  const [copying, setCopying] = useState(false)
  const [closeLoading, setCloseLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const notificationEnabledFromStore = form.userFormAppSetting?.appNotificationEnabled
  const formClosedFromStore = formSetting.manuallyCloseRule?.closed

  // Optimistic local state to prevent switch flicker on Android during async requests
  const [optimisticNotification, setOptimisticNotification] = useState(notificationEnabledFromStore)
  const [optimisticClosed, setOptimisticClosed] = useState(formClosedFromStore)

  useEffect(() => {
    setOptimisticNotification(notificationEnabledFromStore)
  }, [notificationEnabledFromStore])

  useEffect(() => {
    setOptimisticClosed(formClosedFromStore)
  }, [formClosedFromStore])

  const handleEditPress = useCallback(() => {
    if (!isCurrentUserManager) {
      toast.show('无操作权限')
      return
    }
    router.push(`/forms/${formToken}/edit`)
  }, [isCurrentUserManager, router, formToken, toast])

  const handleCooperatorsPress = useCallback(() => {
    if (!isCurrentUserManager) {
      toast.show('无操作权限')
      return
    }
    router.push({ pathname: '/forms/[formToken]/setting/cooperators', params: { formToken: form.token } })
  }, [form.token, router, isCurrentUserManager, toast])

  const handleCopyPress = useCallback(() => {
    if (copying) return
    if (!isCurrentUserManager) {
      toast.show('无操作权限')
      return
    }

    Alert.alert('复制表单后，新表单将出现在表单列表中', undefined, [
      { text: '取消', style: 'cancel' },
      {
        text: '复制',
        style: 'default',
        isPreferred: true,
        onPress: async () => {
          try {
            setCopying(true)
            const { newForm } = await dispatch(copyFormThunk(form.token)).unwrap()
            router.replace({
              pathname: '/forms/[formToken]',
              params: { formToken: newForm.token },
            })
          } catch (error) {
            Alert.alert(get(error, 'message', '复制表单失败'), undefined, [
              {
                text: '确定',
                style: 'default',
                onPress: () => {
                  if (get(error, 'code') === 403) {
                    router.replace('/profile/switchUser')
                  }
                },
              },
            ])
          } finally {
            setCopying(false)
          }
        },
      },
    ])
  }, [copying, dispatch, form.token, isCurrentUserManager, router, toast])

  const [notificationLoading, setNotificationLoading] = useState(false)

  const handleNotificationChange = useCallback(
    async (enabled: boolean) => {
      if (notificationLoading) return

      setOptimisticNotification(enabled)
      setNotificationLoading(true)
      try {
        await dispatch(
          updateUserFormAppSettingThunk({
            formToken: form.token,
            attributes: { formNotificationEnabled: enabled },
          })
        )
        toast.show(enabled ? '提醒已开启' : '提醒已关闭')
      } catch {
        setOptimisticNotification(!enabled)
        toast.show('操作失败')
      } finally {
        setNotificationLoading(false)
      }
    },
    [notificationLoading, dispatch, form.token, toast]
  )

  const handleCloseChange = useCallback(
    async (enabled: boolean) => {
      if (closeLoading) return
      if (!isCurrentUserManager) {
        toast.show('无操作权限')
        return
      }

      setOptimisticClosed(!enabled)
      try {
        setCloseLoading(true)
        await dispatch(
          updateFormSettingThunk({
            formToken: form.token,
            setting: { manuallyCloseRule: { closed: !enabled } },
          })
        )
        toast.show(enabled ? '已开启收集' : '已关闭收集')
      } catch (error) {
        setOptimisticClosed(enabled)
        console.error(error)
        toast.show('操作失败')
      } finally {
        setCloseLoading(false)
      }
    },
    [closeLoading, dispatch, form.token, isCurrentUserManager, toast]
  )

  const handleDeletePress = useCallback(() => {
    if (deleting) return
    if (!isCurrentUserManager) {
      toast.show('无操作权限')
      return
    }

    Alert.alert('删除表单后将移至回收站，可在回收站恢复', undefined, [
      { text: '取消', style: 'cancel' },
      {
        text: '删除',
        style: 'destructive',
        onPress: async () => {
          try {
            setDeleting(true)
            await dispatch(destroyFormThunk(form.token)).unwrap()
            router.back()
          } catch (error) {
            Alert.alert(get(error, 'message', '删除表单失败'), undefined, [
              {
                text: '确定',
                style: 'default',
                onPress: () => {
                  if (get(error, 'code') === 403) {
                    router.replace('/profile/switchUser')
                  }
                },
              },
            ])
          } finally {
            setDeleting(false)
          }
        },
      },
    ])
  }, [deleting, dispatch, form.token, isCurrentUserManager, router, toast])

  const managementSections: TableSection[] = useMemo(
    () => [
      {
        title: '',
        rows: [
          { title: '发布', icon: 'paperplane', onPress: () => dispatch(openPublishSheet({ formToken })) },
          { title: '编辑', icon: 'pencil', onPress: handleEditPress },
          { title: '数据', icon: 'tablecells', onPress: () => router.push(`/forms/${formToken}/entries`) },
          { title: '报表', icon: 'chart.bar', onPress: () => router.push(`/forms/${formToken}/reports`) },
          {
            title: '复制',
            icon: 'doc.on.doc',
            detailText: copying ? '处理中...' : undefined,
            onPress: copying ? undefined : handleCopyPress,
          },
        ],
      },
      {
        title: '',
        rows: [
          {
            title: '收集数据',
            icon: 'checkmark.circle',
            type: 'switch',
            switchValue: !optimisticClosed,
            onValueChange: !isCurrentUserManager ? undefined : handleCloseChange,
          },
          ...(Platform.OS !== 'web'
            ? [
                {
                  title: '新数据提醒',
                  icon: 'bell',
                  type: 'switch' as const,
                  switchValue: optimisticNotification,
                  onValueChange: handleNotificationChange,
                },
              ]
            : []),
          ...(!isAppReviewMode
            ? [
                {
                  title: '团队协作',
                  icon: 'person.2',
                  onPress: handleCooperatorsPress,
                },
              ]
            : []),
        ],
      },
      {
        title: '',
        rows: [
          {
            title: '删除',
            icon: 'trash',
            type: 'action',
            actionType: 'destructive',
            detailText: deleting ? '处理中...' : undefined,
            onPress: deleting ? undefined : handleDeletePress,
          },
        ],
      },
    ],
    [
      dispatch,
      router,
      formToken,
      handleEditPress,
      copying,
      handleCopyPress,
      optimisticClosed,
      isCurrentUserManager,
      handleCloseChange,
      optimisticNotification,
      handleNotificationChange,
      isAppReviewMode,
      handleCooperatorsPress,
      deleting,
      handleDeletePress,
    ]
  )

  return (
    <TableView
      sections={managementSections}
      tableViewStyle="insetGrouped"
      isScrollEnabled={false}
      onContentSizeChange={onTableViewContentSizeChange}
      style={{ backgroundColor: 'transparent', paddingTop: Platform.OS !== 'ios' ? 16 : 0 }} // iOS already has extra padding
    />
  )
}

const IMAGE_ASPECT = 700 / 400

function FormHeaderImage({
  headerImage,
  alt,
  scrollY,
  onImageLoad,
}: {
  headerImage: ImageSourcePropType | null
  alt: string
  scrollY?: SharedValue<number>
  onImageLoad?: () => void
}) {
  const animatedStyle = useAnimatedStyle(() => {
    const y = scrollY?.value ?? 0
    if (y >= 0) {
      return { transform: [{ translateY: y * 0.5 }] }
    }
    const scale = 1 + Math.abs(y) / (IMAGE_ASPECT * 100)
    return { transform: [{ scale }, { translateY: y * 0.5 }] }
  })

  if (!headerImage) return null

  return (
    <Animated.View style={animatedStyle}>
      <View className="aspect-[700/400] overflow-hidden w-full">
        <Image className="w-full h-full" source={headerImage} alt={alt} onLoad={onImageLoad} fadeDuration={0} />
      </View>
    </Animated.View>
  )
}
